/** EDP-00008 — Motion & Emotional Experience framework (mirrors pkg/motionxp) */

export const EDP_MX_ID = 'EDP-00008';

export type Philosophy =
  | 'motion_has_meaning'
  | 'feedback_builds_trust'
  | 'never_surprise_users'
  | 'celebrate_success'
  | 'reduce_anxiety'
  | 'guide_attention'
  | 'support_memory'
  | 'respect_performance'
  | 'accessibility_first'
  | 'ai_feels_helpful';

export type FeedbackKind = 'success' | 'warning' | 'error' | 'info' | 'progress';

export interface MicroInteractionSpec {
  interaction: string;
  duration_ms: number;
  easing: string;
  description: string;
}

export const PHILOSOPHIES: { id: Philosophy; label: string; description: string }[] = [
  { id: 'motion_has_meaning', label: 'Motion Has Meaning', description: 'Animation communicates state change — never decorative-only' },
  { id: 'feedback_builds_trust', label: 'Feedback Builds Trust', description: 'Every action receives visible acknowledgment within 100ms' },
  { id: 'never_surprise_users', label: 'Never Surprise Users', description: 'Transitions are predictable; no unexpected layout shifts' },
  { id: 'celebrate_success', label: 'Celebrate Success', description: 'Milestones receive proportionate positive reinforcement' },
  { id: 'reduce_anxiety', label: 'Reduce Anxiety', description: 'Loading states explain what is happening and estimated progress' },
  { id: 'guide_attention', label: 'Guide Attention', description: 'Motion directs focus to the next required action' },
  { id: 'support_memory', label: 'Support Memory', description: 'Spatial consistency aids recall of object locations' },
  { id: 'respect_performance', label: 'Respect Performance', description: 'Animations ≤400ms; GPU-friendly transforms only' },
  { id: 'accessibility_first', label: 'Accessibility First', description: 'prefers-reduced-motion honored; keyboard equivalence' },
  { id: 'ai_feels_helpful', label: 'AI Feels Helpful', description: 'AI states show reasoning progress, not black-box spinners' },
];

export const MICRO_INTERACTIONS = [
  'Button Press', 'Hover', 'Focus', 'Selection', 'Drag', 'Drop', 'Expand', 'Collapse',
  'Tab Switch', 'Filtering', 'Searching', 'Sorting', 'Inline Editing', 'Saving',
  'Publishing', 'Approval', 'Undo', 'Redo',
];

export const MICRO_INTERACTION_SPECS: MicroInteractionSpec[] = [
  { interaction: 'button_press', duration_ms: 100, easing: 'ease-out', description: 'Scale 0.98 + shadow reduction' },
  { interaction: 'hover', duration_ms: 150, easing: 'ease', description: 'Background/border color shift' },
  { interaction: 'focus', duration_ms: 0, easing: 'ease', description: '2px focus ring' },
  { interaction: 'selection', duration_ms: 150, easing: 'ease-out', description: 'Background highlight + check' },
  { interaction: 'expand', duration_ms: 250, easing: 'ease-out', description: 'Height + opacity expand' },
  { interaction: 'tab_switch', duration_ms: 200, easing: 'ease', description: 'Cross-fade + underline slide' },
  { interaction: 'saving', duration_ms: 300, easing: 'ease', description: 'Progress bar + checkmark' },
  { interaction: 'approval', duration_ms: 250, easing: 'ease-out', description: 'Card slide-in + action pulse' },
];

export const AI_INTERACTIONS = [
  'AI Thinking', 'Streaming Responses', 'Reasoning Progress', 'Recommendation Updates',
  'Confidence Changes', 'Simulation Progress', 'Learning Progress', 'Approval Feedback', 'AI Completion',
];

export const FEEDBACK_TYPES = [
  'Success', 'Warning', 'Error', 'Information', 'Background Job', 'Synchronization',
  'Retry', 'Recovery', 'Validation', 'Progress',
];

export const LOADING_PATTERNS = [
  'Skeleton Screens', 'Progress Bars', 'Estimated Time', 'Step Indicators',
  'Streaming Updates', 'Incremental Loading', 'Lazy Loading', 'Background Refresh',
];

export const SUCCESS_MILESTONES = [
  'First Product Published', 'First AI Recommendation Accepted', 'First Campaign Created',
  'Revenue Milestones', 'Team Achievements', 'Workflow Completion', 'Bulk Operations',
];

export const ERROR_FIELDS = [
  'Clear Explanation', 'Business Impact', 'Recommended Recovery', 'Retry Option',
  'AI Assistance', 'Support Link', 'Correlation ID',
];

export const BACKGROUND_JOBS = [
  'Synchronization', 'Imports', 'Exports', 'AI Generation', 'Bulk Updates',
  'Publishing', 'Forecasting', 'Analysis', 'Queue Status',
];

export const NOTIFICATION_CHANNELS = [
  'Toast', 'Persistent Alert', 'Activity Feed', 'Timeline Event',
  'AI Suggestion', 'Marketplace Event', 'Role-based Notification',
];

export const TRANSITIONS = [
  { type: 'Page', duration_ms: 250 },
  { type: 'Panel', duration_ms: 200 },
  { type: 'Drawer', duration_ms: 300 },
  { type: 'Dialog', duration_ms: 200 },
  { type: 'Card Expansion', duration_ms: 250 },
  { type: 'Timeline Update', duration_ms: 150 },
  { type: 'Workspace Switch', duration_ms: 200 },
];

export const EMOTIONAL_GOALS = [
  'Confidence', 'Trust', 'Achievement', 'Curiosity', 'Calmness', 'Clarity', 'Momentum', 'Control',
];

export const MOTION_ACCESSIBILITY = [
  'Reduced Motion', 'Disable Non-essential Animations', 'Keyboard Equivalence',
  'Screen Reader Compatibility', 'Timing Controls',
];

export const ANALYTICS_METRICS = [
  'Interaction Completion', 'Animation Performance', 'User Satisfaction', 'Perceived Speed',
  'Task Confidence', 'AI Trust', 'Error Recovery', 'Workflow Success',
];

export interface MotionDashboard {
  philosophies: number;
  micro_interactions: number;
  ai_interactions: number;
  feedback_types: number;
  loading_patterns: number;
  success_milestones: number;
  error_fields: number;
  background_jobs: number;
  notification_channels: number;
  transitions: number;
  emotional_goals: number;
  max_duration_ms: number;
}

export function buildMotionDashboard(): MotionDashboard {
  return {
    philosophies: PHILOSOPHIES.length,
    micro_interactions: MICRO_INTERACTIONS.length,
    ai_interactions: AI_INTERACTIONS.length,
    feedback_types: FEEDBACK_TYPES.length,
    loading_patterns: LOADING_PATTERNS.length,
    success_milestones: SUCCESS_MILESTONES.length,
    error_fields: ERROR_FIELDS.length,
    background_jobs: BACKGROUND_JOBS.length,
    notification_channels: NOTIFICATION_CHANNELS.length,
    transitions: TRANSITIONS.length,
    emotional_goals: EMOTIONAL_GOALS.length,
    max_duration_ms: 400,
  };
}
